import {Suspense, lazy, useEffect} from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN, ADMIN_PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
import SplashScreen from '../components/SplashScreen';
import {useSelector} from "../redux/store";
import { ADMIN_ROLE_UUID, REGISTER_ROLE_UUID, USER_ROLE_UUID } from 'src/constants/user-role-constants';
import ReactGA from "react-ga4";

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={!pathname.includes('login') || !pathname.includes('register')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const {userRole} = useSelector((state) => state.user);

  useEffect(() => {
    if (!(window.location.pathname + window.location.search).includes('admin')) {
      ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search, title: window.location.pathname + window.location.search });// Google Analytics...
    }
  }, [window.location.pathname + window.location.search])

  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: '/register',
      element: (
          <RoleBasedGuard roles={[REGISTER_ROLE_UUID]}>
            <DashboardLayout />
          </RoleBasedGuard>
      ),
      children: [
        { path: '', element: <RegisterUser /> },
      ],
    },
    {
      path: "/",
      element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
      ),
      children: [
        { element: <Navigate to={userRole ? userRole === REGISTER_ROLE_UUID ? "/register" : userRole !== ADMIN_ROLE_UUID ? PATH_AFTER_LOGIN : ADMIN_PATH_AFTER_LOGIN : "/loading"} replace />, index: true }]
    },
    {
      path: '/admin',
      element: (
        <RoleBasedGuard hasContent roles={[ADMIN_ROLE_UUID]}>
          <DashboardLayout />
        </RoleBasedGuard>
      ),
      children: [
        { element: <Navigate to={ADMIN_PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'create-question/multichoice/translation', element: <CreateMultichoiceQuestion /> },
        { path: 'create-question/basic/translation', element: <CreateBasicTranslationQuestion /> },
        { path: 'create-question/multichoice/annotation', element: <CreateMultichoiceAnnotationQuestion /> },
        { path: 'create-question/yes-no', element: <CreateYesNoQuestion /> },
        { path: 'create-question/vc-validator', element: <CreateVCValidatorQuestion /> },
        { path: 'create-question/transcription', element: <CreateTranscriptionQuestion /> },
        { path: 'create-question/language-skills/paragraph', element: <CreateParagraphQuestion /> },
        { path: 'create-question/language-skills/improvement', element: <CreateParapraphImprovementQuestion /> },
        { path: 'create-question/language-skills/summary', element: <CreateParapraphSummaryQuestion /> },
        { path: 'create-question/language-skills/parallel', element: <CreateParallelQuestion /> },
        { path: 'create-question/language-skills/cthinking', element: <CreateCriticalThinkingQuestion /> },
        { path: 'create-question/language-skills/reading', element: <CreateReadingQuestion /> },
        { path: 'create-test/transcription', element: <CreateTranscriptionTest /> },
        { path: 'create-test/annotation', element: <CreateAnnotationTest /> },
        { path: 'create-test/yesno', element: <CreateYesnoTest /> },
        { path: 'create-test/translation', element: <CreateTranslationTest /> },
        { path: 'create-test/language', element: <CreateLanguageTest /> },
        { path: 'list-language-skills-results', element: <RavUsersTestResults /> },
        { path: 'evaluate-language-test/:testId/:userId', element: <EvaluateLanguageTest /> },
        { path: 'browse-voice-collection-tasks', element: <BrowseVoiceCollectionTasks /> },
        { path: 'create-voice-collection/form', element: <CreateVCForm /> },
        { path: 'create-voice-collection/task', element: <CreateVoiceCollectionTask /> },
        { path: 'create-voice-collection-validator-test', element: <CreateVoiceCollectionValidatorTest /> },
        { path: 'voice-collection-evaluation', element: <VoiceCollectionEvaluation /> },
        { path: 'admin-settings', element: <AdminSettingsComponent /> },
        { path: 'block-users', element: <BlockUserList /> },
        { path: 'browse-tests', element: <AdminBrowseTests /> },
        { path: 'browse-questions', element: <BrowseQuestions /> },
        { path: 'upload-excel', element: <UploadExcel /> },
        { path: 'create-job-type', element: <CreateJobType /> },
        { path: 'job-type-list', element: <JobTypeList /> },
        { path: 'user-list', element: <UserList /> },
        { path: 'auth-settings', element: <AdminAuthSettings /> },
        { path: 'browse-voice-collection-forms', element: <BrowseVoiceCollectionForms /> },
        { path: 'browse-voice-collection-form-evaluations', element: <BrowseVoiceCollectionFormEvaluations /> },
        { path: 'browse-creativity-skills-test', element: <BrowseCreativitySkillsTest /> },
        { path: 'voice-collection-evaluation-results', element: <VoiceCollectionEvaluationResults /> },
        { path: 'add-remove-domains', element: <AddNewDomain /> },
        {
          path: '',
          children: [
            { element: <Navigate to="/account" replace />, index: true },
            { path: 'account', element: <UserAccount /> },
          ],
        },
      ],
    },

    // Dashboard Routes
    {
      path: '/user',
      element: (
        <RoleBasedGuard hasContent roles={[USER_ROLE_UUID]}>
          <DashboardLayout />
        </RoleBasedGuard>
      ),
      children: [
        { element: <Navigate to={userRole !== ADMIN_ROLE_UUID ? PATH_AFTER_LOGIN : ADMIN_PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'my-test-results', element: <MyTestResults /> },
        { path: 'tests-to-do', element: <TestToDo /> },
        { path: 'certification', element: <Certification /> },
        { path: 'terms-legal', element: <TermsLegal /> },
        { path: 'browse-test', element: <BrowseTest /> },
        { path: 'job-list', element: <JobList /> },
        { path: 'take-test', element: <TakeTest /> },
        { path: 'my-qualifications', element: <MyQualifications/> },
        { path: 'voice-collection-form', element: <UserVoiceCollectionForm/> },
        { path: 'validator-take-test', element: <UserValidatorTakeTest/> },
        { path: 'vc-evaluation-list', element: <BrowseVoiceCollectionFormEvaluations/> },
        { path: 'voice-collection-evaluation', element: <VoiceCollectionEvaluation /> },
        { path: 'browse-voice-collection-form-evaluations', element: <BrowseVoiceCollectionFormEvaluations /> },
        { path: 'browse-creativity-skills-test', element: <BrowseCreativitySkillsTest /> },
        { path: 'evaluate-language-test/:testId/:userId', element: <EvaluateLanguageTest /> },

        {
          path: 'user',
          children: [
            { element: <Navigate to="/user/account" replace />, index: true },
            { path: 'account', element: <UserAccount /> },
          ],
        },
      ],
    },
    {
      path: 'invitation',
      children: [
        {
          path: 'accept/:uuid',
          element: (
              <NoodleInvitation type="Accept" />
          ),
        },
        {
          path: 'reject/:uuid',
          element: (
              <NoodleInvitation type="Reject"/>
          ),
        },
      ],
    },
    { path: '404', element: <Page404 /> },
    { path: 'loading', element: <SplashScreen /> },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

//NOODLE INVITATION
const NoodleInvitation = Loadable(lazy(() => import('../pages/noodleInvitation/NoodleInvitation')))

// RAVIOLI ADMIN
const CreateMultichoiceQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateMultichoiceTranslationQuestion')));
const CreateBasicTranslationQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateBasicTranslationQuestion')));
const CreateMultichoiceAnnotationQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateMultichoiceAnnotationQuestion')));
const CreateYesNoQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateYesNoQ')));
const BrowseQuestions = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-BrowseQuestions')));
const BrowseVoiceCollectionForms = Loadable(lazy(() => import('../pages/adminDashboard/voice-collection-management/Rav-BrowseVoiceCollectionForms')));
const BrowseVoiceCollectionFormEvaluations = Loadable(lazy(() => import('../pages/adminDashboard/voice-collection-management/Rav-BrowseVoiceCollectionFormEvaluations')));
const BrowseCreativitySkillsTest = Loadable(lazy(() => import('../pages/adminDashboard/test-management/Rav-BrowseCreativitySkillsTest')));
const AdminBrowseTests = Loadable(lazy(() => import('../pages/adminDashboard/test-management/Rav-AdminBrowseTests')));
const CreateTranscriptionQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateTranscriptionQuestion')));
const CreateParagraphQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateParagraphQuestion')));
const CreateParallelQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateParallelQuestion')));
const CreateCriticalThinkingQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateCriticalThinkingQuestion')));
const CreateReadingQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateReadingQuestion')));
const CreateParapraphImprovementQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateImprovementQuestion')));
const CreateParapraphSummaryQuestion = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-CreateSummaryQuestion')));
const CreateTranscriptionTest = Loadable(lazy(() => import('../pages/adminDashboard/test-management/Rav-CreateTranscriptionTest')));
const CreateAnnotationTest = Loadable(lazy(() => import('../pages/adminDashboard/test-management/Rav-CreateAnnotationTest')));
const CreateLanguageTest = Loadable(lazy(() => import('../pages/adminDashboard/test-management/Rav-CreateLanguageTest')));
const EvaluateLanguageTest = Loadable(lazy(() => import('../pages/adminDashboard/Rav-EvaluateLanguageTest')));
const RavUsersTestResults = Loadable(lazy(() => import('../pages/adminDashboard/Rav-UsersTestResults')));
const VoiceCollectionEvaluation = Loadable(lazy(() => import('../pages/adminDashboard/voice-collection-management/Rav-VoiceCollectionEvaluation')));
const CreateYesnoTest = Loadable(lazy(() => import('../pages/adminDashboard/test-management/Rav-CreateYesNoTest')));
const CreateVoiceCollectionTask = Loadable(lazy(() => import('../pages/adminDashboard/voice-collection-management/Rav-createVCTask')));
const CreateVoiceCollectionValidatorTest = Loadable(lazy(() => import('../pages/adminDashboard/voice-collection-management/Rav-CreateVCValidatorTest')));
const UploadExcel = Loadable(lazy(() => import('../pages/adminDashboard/question-management/Rav-UploadExcel')));
const CreateTranslationTest = Loadable(lazy(() => import('../pages/adminDashboard/test-management/Rav-CreateTranslationTest')));
const AdminSettingsComponent = Loadable(lazy(() => import('../pages/adminDashboard/admin-settings/Rav-AdminSettings')));
const CreateJobType = Loadable(lazy(() => import("../pages/adminDashboard/Rav-CreateJobType")));
const BlockUserList = Loadable(lazy(() => import("../pages/adminDashboard/admin-settings/Rav-BlockUserList")));
const JobTypeList = Loadable(lazy(() => import("../pages/adminDashboard/Rav-JobTypeList")));
const AdminAuthSettings = Loadable(lazy(() => import("../pages/adminDashboard/admin-settings/Rav-AuthPage")));
const CreateVCForm = Loadable(lazy(() => import("../pages/adminDashboard/voice-collection-management/Rav-CreateVoiceCollectionForm")));
const BrowseVoiceCollectionTasks = Loadable(lazy(() => import("../pages/adminDashboard/voice-collection-management/Rav-BrowseVoiceCollectionTasks")));
const VoiceCollectionEvaluationResults = Loadable(lazy(() => import("../pages/adminDashboard/voice-collection-management/Rav-VoiceCollectionEvaluationResults")));
const CreateVCValidatorQuestion = Loadable(lazy(() => import("../pages/adminDashboard/question-management/Rav-CreateVCValidatorQuestion")));
const AddNewDomain = Loadable(lazy(() => import("../pages/adminDashboard/Rav-AddNewDomain")));

// RAVIOLI USER
const MyTestResults = Loadable(lazy(() => import('../pages/dashboard/Rav-MyTestResults')));
const TestToDo = Loadable(lazy(() => import('../pages/dashboard/Rav-TestToDo')));
const Certification = Loadable(lazy(() => import('../pages/dashboard/Rav-Certification')));
const TermsLegal = Loadable(lazy(() => import('../pages/dashboard/Rav-TermsLegal')));
const BrowseTest = Loadable(lazy(() => import('../pages/dashboard/Rav-BrowseTests')));
const JobList = Loadable(lazy(() => import('../pages/dashboard/Rav-JobList')));
const TakeTest = Loadable(lazy(() => import('../pages/dashboard/Rav-takeTest')));
const MyQualifications = Loadable(lazy(() => import('../pages/dashboard/Rav-MyQualifications')));
const UserVoiceCollectionForm = Loadable(lazy(() => import('../pages/dashboard/Rav-UserVoiceCollectionForm')));
const UserValidatorTakeTest = Loadable(lazy(() => import('../pages/dashboard/Rav-ValidatorTakeTest')));

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));

// ECOMMERCE
const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));

// INVOICE
const InvoiceList = Loadable(lazy(() => import('../pages/dashboard/InvoiceList')));
const InvoiceDetails = Loadable(lazy(() => import('../pages/dashboard/InvoiceDetails')));
const InvoiceCreate = Loadable(lazy(() => import('../pages/dashboard/InvoiceCreate')));
const InvoiceEdit = Loadable(lazy(() => import('../pages/dashboard/InvoiceEdit')));

// BLOG
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));

// USER
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/adminDashboard/Rav-UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const RegisterUser = Loadable(lazy(() => import('../pages/auth/RegisterUser')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));

// APP
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// MAIN
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
